// Currency Formatting Utilities
// Functions for formatting and parsing currency values.
// All values are stored as whole numbers (no cents) for this application.

import type { CurrencyValue } from './sow.types';

// Intl formatter for USD currency
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

// Format a number as USD currency string
// @param value - The numeric value to format
// @returns Formatted string (e.g., "$1,234")
export function formatCurrency(value: CurrencyValue): string {
  return currencyFormatter.format(value);
}

// Parse a currency string to a number
// Handles various input formats: "$1,234", "1234", "1,234.56", etc.
// @param value - The string to parse
// @returns Parsed numeric value (always >= 0)
export function parseCurrency(value: string): CurrencyValue {
  if (!value || typeof value !== 'string') {
    return 0;
  }

  // Remove currency symbols, commas, spaces
  const cleaned = value.replace(/[$,\s]/g, '');

  // Parse as float and round to whole number
  const parsed = parseFloat(cleaned);

  // Return 0 for NaN, negative values become 0
  if (isNaN(parsed)) {
    return 0;
  }

  return Math.max(0, Math.round(parsed));
}

// Format a percentage
// @param value - The decimal value (e.g., 0.05 for 5%)
// @param decimals - Number of decimal places
// @returns Formatted string (e.g., "5%")
export function formatPercentage(value: number, decimals: number = 0): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

// Pattern for validating currency input while typing
// Allows: digits, commas, periods, dollar sign
const CURRENCY_INPUT_PATTERN = /^[$,.\d]*$/;

// Check if a string is valid currency input (for filtering keystroke input)
// @param value - The string to validate
// @returns True if the value contains only valid currency characters
export function isValidCurrencyInput(value: string): boolean {
  return CURRENCY_INPUT_PATTERN.test(value);
}
